import type {
  ButtonNavItem,
  ColorSchemeSwitcherNavItem,
  LanguageSwitcherNavItem,
  NavigationItemAlignment,
  NavigationItemDirection,
  NavigationItemIcon,
  NavigationItemText,
  NavigationPosition,
  NavigationResolution,
  NavigatorType,
  PageNavItem,
  UrlNavItem,
} from '@unifyapps/defs/types/interface';
import type { BlockThemeEnum } from '@unifyapps/defs/types/styleObject';
import type { User } from '@unifyapps/network/generated/models/user';
import type { PopoverContentProps } from '@unifyapps/ui/components/Popover/PopoverContent/types';
import type React from 'react';
import type { CSSProperties, Dispatch, RefObject, SetStateAction } from 'react';
// platform rail popover state — defined in the platform namespace, referenced here only because the
// shared slot contracts (NavigationItemSlotPropsType / SideNavProps.slots.top) carry it through.
import type { NavItemPopoverState } from './components/platform/types';
import type { UseAppSideNavStateProps } from './variants/SideNavigation/hooks/useAppSideNavState';

export type NavItemNavigationOptions = {
  openInNewTab?: boolean;
  // later: prefetch?: boolean; rel?: string; etc.
};

export interface NavItemStartIconProps {
  icon?: string;
  activeIcon?: string;
}

export interface NavItemEndIconProps {
  endIcon?: string;
  activeEndIcon?: string;
}

export type NavItemIconProps = NavItemStartIconProps & NavItemEndIconProps;

export type NavItem = NavItemIconProps & {
  type: 'URL';
  id: string;
  name: string;
  url: string;
  // When showing the navigation items in the preview mode(example: in the interface
  // builder settings),we want to make the items readonly to disable routing on click
  readonly?: boolean;
  getIsPathActive?: ({
    activePathnames,
    path,
  }: {
    activePathnames: string[];
    path: string;
  }) => boolean;
  navigationOptions?: NavItemNavigationOptions;
  /** @deprecated Use `navigationOptions.openInNewTab` instead. */
  openInNewTab?: boolean;
  renderEndDecorator?: () => React.ReactNode;
  moduleId?: string;
  isInvalidUrl?: boolean;
};

export type GroupedNavItem = NavItem | NavItemGroup | NavApplicationItemType;
export type NavItemGroup = NavItemStartIconProps & {
  type: 'GROUP';
  id: string;
  name: string;
  items: GroupedNavItem[];
  isGroupOpen?: boolean;
  renderEndDecorator?: () => React.ReactNode;
};

export type NavUserMenu = {
  type: 'USER_MENU';
  id: string;
  user: Pick<User, 'name' | 'email'>;
  customerName?: string;
  disabled?: boolean;
  onClick?: () => void;
  popupProps?: {
    placement: PopoverContentProps['placement'];
    ignoreBoundary?: boolean;
    maxWidth?: number;
    minWidth?: number;
    className?: string;
    transform?: boolean;
    scheme?: 'light' | 'dark';
  };
  // Optional: Side/Bottom navigation renders this inside a Popover. The
  // Drawer variant has no popup and reads only `user`, so consumers that
  // do not surface a menu may omit it.
  MenuList?: React.FC<{ activePathnames?: string[]; onClose?: () => void }>;
  menuItems?: (
    | UrlNavItem
    | PageNavItem
    | ButtonNavItem
    | ColorSchemeSwitcherNavItem
    | LanguageSwitcherNavItem
  )[];
};

export type BaseNavAction = NavItemEndIconProps & {
  type: 'ACTION';
  action?: string;
  id: string;
  moduleId?: string;
  icon: string;
  iconClassName?: string;
  activeIcon?: string;
  name: string;
  shortcutKeys?: string;
  onClick: (args: { onOpen: () => void; onClose: () => void }) => void;
  renderEndDecorator?: () => React.ReactNode;
  // For InterfaceBuilderSideNav, we want to show multiple items in active state
  // at same time. Give priority to this active state over the default active
  // state(derived from activePathnames) for this particular action item
  activeDisabledControllers?: {
    disabled?: boolean;
    active?: boolean;
  };
};

export type NotificationActionPropsType = {
  unopenedNotificationsCount: number;
  // when true, a non-zero count is an error signal (builder DevTools buffered errors): the nav item
  // paints its icon + button red, not just the badge. omitted/false = neutral count (e.g. notifications).
  isError?: boolean;
};

export type NotificationNavAction = BaseNavAction & {
  actionProps?: NotificationActionPropsType;
  action?: 'showNotifications';
};

export type HelpNavAction = BaseNavAction & {
  action?: 'showHelp';
};

export type NavAction = NotificationNavAction | HelpNavAction | BaseNavAction; // can add more actions as needed

export type NavLabel = NavItemEndIconProps & {
  type: 'LABEL';
  id: string;
  name: string;
};

export type NavApplicationItemType = NavItemIconProps & {
  type: 'APPLICATION_PAGE';
  id: string;
  name: string;
  url: string;
  navigationOptions?: NavItemNavigationOptions;
  /** @deprecated Use `navigationOptions.openInNewTab` instead. */
  openInNewTab?: boolean;
  moduleId?: string;
  readonly?: boolean;
};

export type NavCustomRenderer = NavItemEndIconProps & {
  type: 'CUSTOM';
  id: string;
  renderer: () => React.ReactNode;
};

export type NavigationItem =
  | NavItem
  | NavItemGroup
  | NavUserMenu
  | NavAction
  | NavLabel
  | NavCustomRenderer
  | NavApplicationItemType
  | ButtonNavItem
  | ColorSchemeSwitcherNavItem
  | LanguageSwitcherNavItem;

export type NavItems = NavigationItem[];

export type Navigation = {
  primary: NavItems;
  secondary?: NavItems;
};

export type App = { name: string; logo: { small: string; large?: string } };

export enum NavigationItemSelectionVariantEnum {
  Stroke = 'stroke',
  Solid = 'solid',
}

export enum NavigationItemLabelVariantEnum {
  Expandable = 'expandable',
  Upfront = 'upfront',
  Tooltip = 'tooltip',
  None = 'none',

  // For platform SideNav
  Platform = 'platform',
}

export enum NavigationBarType {
  Fixed = 'fixed',
  Floating = 'floating',
}

export enum NavigationPositionEnum {
  Start = 'start',
  Bottom = 'bottom',
}

// XS is 52px and SM is 56px in Navbar Width
export enum ExpandableNavigationVariant {
  XS = 'xs',
  SM = 'sm',
}

export type NavigationItemSlotPropsType = {
  navigationItem: {
    position?: NavigationPosition;
    resolution?: NavigationResolution;
    labelVariant?: NavigationItemLabelVariantEnum;
    selectionVariant?: NavigationItemSelectionVariantEnum;
    expanded?: boolean;
    setExpanded?: Dispatch<SetStateAction<boolean>>;
    expandableNavVariant?: ExpandableNavigationVariant;
    direction?: NavigationItemDirection;
    alignment?: NavigationItemAlignment;
    typographyClassName?: string;
    icon?: Omit<NavigationItemIcon, 'colors'>;
    navItemClassName?: string;
    navItemStyles?: CSSProperties;
    navItemBorderRadiusClassName?: string;
    navItemBorderRadiusStyles?: CSSProperties;
    hideIcon?: boolean;
    hideActionNavLabel?: boolean;
    expandedGroupId?: string | null;
    setExpandedGroupId?: Dispatch<SetStateAction<string | null>>;
    // collapsed-rail popover open slot — single shared value so only one popover (a nav group's L2
    // popover or the platform ProjectSwitcher) is open at a time. See NavItemPopoverState.
    navItemPopover?: NavItemPopoverState;
    setNavItemPopover?: Dispatch<SetStateAction<NavItemPopoverState>>;
  };
  container?: {
    className?: string;
  };
  borderColorClassName?: string;
  theme?: BlockThemeEnum;
};

export type NavigationItemAppearance = {
  selectionVariant: NavigationItemSelectionVariantEnum;
  labelVariant: NavigationItemLabelVariantEnum;
  direction?: NavigationItemDirection;
  alignment?: NavigationItemAlignment;
  text?: Omit<NavigationItemText, 'colors'>;
  icon?: Omit<NavigationItemIcon, 'colors'>;
};

export type NavigationPropsType = {
  navigation: Navigation;
  navigationItemAppearance?: NavigationItemAppearance;
  floatingOffset?: string;
  navigatorType?: NavigatorType;
  navigationBarType?: NavigationBarType;
  position: NavigationPosition;
  theme?: BlockThemeEnum;
  app?: App;
  resolution?: NavigationResolution;
  expandableNavVariant?: ExpandableNavigationVariant;
  disableNavItemClick?: boolean;
};

export type NavigationStylesType = {
  style?: CSSProperties;
  containerStyles?: CSSProperties;
  heightStyles?: CSSProperties;
  widthStyles?: CSSProperties;
  navItemStyles?: CSSProperties;
  navItemsGapStyles?: CSSProperties;
  navItemBorderRadiusStyles?: CSSProperties;
};

export type NavigationClassNamesType = {
  className: string;
  containerClassName?: string;
  heightClassName?: string;
  widthClassName?: string;
  borderColorClassName?: string;
  rootClassName?: string;
  typographyClassName?: string;
  navItemClassName?: string;
  navItemsGapClassName?: string;
  navItemBorderRadiusClassName?: string;
};

export interface SideNavProps {
  rootClassName?: string;
  disableHoverListeners?: boolean;
  controlledState?: UseAppSideNavStateProps['controlledState'];
  navigationProps: NavigationPropsType;
  navigationStyles?: NavigationStylesType;
  navigationClassNames: NavigationClassNamesType;
  sideNavRef?: RefObject<HTMLDivElement | null>;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;

  // route props, taking from props - since getting these props from useParams triggers a re-render on any route change
  pathname: string;
  projectSlug?: string;
  slots?: {
    top?: ({
      expanded,
      setExpanded,
      navItemPopover,
      setNavItemPopover,
    }: {
      expanded: boolean;
      setExpanded: (expanded: boolean) => void;
      // shared collapsed-rail popover slot — lets a top-slot popover (e.g. ProjectSwitcher) coordinate
      // with the nav-group popovers so only one is open at a time.
      navItemPopover?: NavItemPopoverState;
      setNavItemPopover?: Dispatch<SetStateAction<NavItemPopoverState>>;
    }) => React.ReactNode;
  };
}

export type BottomNavProps = SideNavProps;
